import { Flex } from "../../../components/flex/Flex"
import Text from "../../../components/Text"
import { ColorType, IconType, theme } from "../../../styles/stitches.config"
import { SPriceInfoContainer, SPriceInfoContent, SPricePointsContainer } from "./DailyPrices.styled"
import * as icons from "../../../images/xd/icons"
import { useWorstToBestPriceLevels } from "../../../components/chart/hooks"
import { useMemo } from "react"
import { FormattedUnit } from "../../../components/FormattedUnit"
import { getCurrency } from "../../../i18n/config"
import { useIsMaxMd } from "../../../hooks/mediaHooks"
import { useTranslation } from "react-i18next"
import { EnergyUnit } from "@nano-portal/shared"

export const PriceInfo = ({
  icon,
  asCard,
  label,
  price,
}: {
  icon: IconType
  asCard?: boolean
  label: string
  price?: number
}) => {
  const Icon = icons[icon]
  const isMobileVersion = useIsMaxMd()

  return (
    <SPriceInfoContainer popOut={asCard}>
      <SPriceInfoContent padded={!asCard}>
        <Icon width={theme.space.s12.value} height={theme.space.s12.value} display="inline" />

        <Flex direction="column" align={isMobileVersion ? "center" : "start"} css={{ gap: theme.sizes.s1 }}>
          <Text
            type="headerH3"
            css={{ fontSize: isMobileVersion ? theme.sizes.s2_5 : theme.sizes.s3, textTransform: "uppercase" }}
          >
            {label}
          </Text>

          {price !== undefined ? (
            <FormattedUnit
              unit={`${getCurrency().symbol}/${EnergyUnit.kWh}`}
              value={price}
              valueTextType="textsSmall"
              valueColor="textsAlt"
              unitTextType="textsSmall"
              unitColor="textsAlt"
              unitFontSize={11}
            />
          ) : (
            <Text type="textsSmall" color="textsAlt">
              ---
            </Text>
          )}
        </Flex>
      </SPriceInfoContent>
    </SPriceInfoContainer>
  )
}

export const getIconProps = (
  faceState: ReturnType<typeof useWorstToBestPriceLevels>
): { icon: IconType; color: ColorType } => {
  switch (faceState) {
    case "BEST":
      return { icon: "FaceBest", color: "secondary" }
    case "GOOD":
      return { icon: "FaceGood", color: "good" }
    case "NEUTRAL":
      return { icon: "FaceNeutral", color: "warningLight" }
    case "BAD":
      return { icon: "FaceBad", color: "bad" }
    case "WORST":
      return { icon: "FaceWorst", color: "errors" }
    default:
      return { icon: "FaceQuestion", color: "textsAlt" }
  }
}

export const PricePoints = ({
  lowestPrice,
  currentPrice,
  highestPrice,
}: {
  lowestPrice: number
  highestPrice: number
  currentPrice?: number
}) => {
  const isMobileVersion = useIsMaxMd()
  const faceState = useWorstToBestPriceLevels(lowestPrice, highestPrice, currentPrice)
  const currentFaceProps = useMemo(() => getIconProps(faceState), [faceState])
  const { t } = useTranslation(["microsite"])

  return (
    <SPricePointsContainer>
      {isMobileVersion ? (
        <>
          <PriceInfo icon="FaceWorst" label={t("microsite:daily_prices_highest_price")} price={highestPrice} />
          <PriceInfo
            icon={currentFaceProps.icon as IconType}
            asCard
            label={t("microsite:daily_prices_current_price")}
            price={currentPrice}
          />
          <PriceInfo icon="FaceBest" label={t("microsite:daily_prices_lowest_price")} price={lowestPrice} />
        </>
      ) : (
        <>
          <PriceInfo
            icon={currentFaceProps.icon as IconType}
            asCard
            label={t("microsite:daily_prices_current_price")}
            price={currentPrice}
          />
          <PriceInfo icon="FaceBest" label={t("microsite:daily_prices_lowest_price")} price={lowestPrice} />
          <PriceInfo icon="FaceWorst" label={t("microsite:daily_prices_highest_price")} price={highestPrice} />
        </>
      )}
    </SPricePointsContainer>
  )
}
